<script setup>
import { ref } from 'vue';

defineProps({
  type: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
});

const show = ref(true);
</script>

<template>
  <div
    v-if="show"
    class="relative rounded-lg border p-4"
    :class="{
      'border-blue-100 bg-blue-50': type === 'info',
      'border-green-100 bg-green-50': type === 'success',
      'border-orange-100 bg-orange-50': type === 'warning',
      'border-rose-100 bg-rose-50': type === 'error'
    }">
    <div class="flex">
      <div class="flex shrink-0 items-center">
        <svg
          v-if="type === 'error'"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-rose-400"
          viewBox="0 0 24 24"
          fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
            clip-rule="evenodd" />
        </svg>

        <svg
          v-if="type === 'info'"
          class="h-5 w-5 text-blue-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000
            1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75
            1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clip-rule="evenodd" />
        </svg>

        <svg
          v-if="type === 'warning'"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-orange-400"
          viewBox="0 0 24 24"
          fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
            clip-rule="evenodd" />
        </svg>

        <svg
          v-if="type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-green-400"
          viewBox="0 0 24 24"
          fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
            clip-rule="evenodd" />
        </svg>
      </div>
      <div class="ml-3 flex-1 md:flex md:justify-between">
        <p
          class="text-sm"
          :class="{
            'text-blue-700': type === 'info',
            'text-green-700': type === 'success',
            'text-orange-700': type === 'warning',
            'text-rose-700': type === 'error'
          }">
          {{ message }}
        </p>
        <p class="absolute right-1.5 top-1.5 mt-3 text-sm md:ml-6 md:mt-0">
          <span
            class="cursor-pointer whitespace-nowrap font-medium"
            :class="{
              'text-blue-700 hover:text-blue-600': type === 'info',
              'text-green-700 hover:text-green-600': type === 'success',
              'text-orange-700 hover:text-orange-600': type === 'warning',
              'text-rose-700 hover:text-rose-600': type === 'error'
            }"
            @click="show = false">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-4 w-4">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18 18 6M6 6l12 12" />
            </svg>
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
